import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../utils";
import { useTranslation } from "react-i18next";
import { Typography } from "../Typography";
import { Button } from "../Button";

type TPaginationProps = {
  totalRecordCount: number;
  page: number; // current page (0-based)
  pageCount: number; // total number of pages
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRows: number) => void;
  disablePrev: boolean;
  disableNext: boolean;
  className?: string;
  id?: string;
};

const CommonPaginationCard = ({
  //  totalRecordCount,
  page,
  pageCount,
  //rowsPerPage,
  onPageChange,
  //  onRowsPerPageChange,
  disablePrev,
  disableNext,
  className,
  id,
}: TPaginationProps) => {
  const { t } = useTranslation("common");

  // Helper → generate page numbers with ellipsis if needed
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // show at most 5 page numbers
    if (pageCount <= maxVisible) {
      for (let i = 0; i < pageCount; i++) pages.push(i);
    } else {
      if (page <= 2) {
        pages.push(0, 1, 2, "...", pageCount - 1);
      } else if (page >= pageCount - 3) {
        pages.push(0, "...", pageCount - 3, pageCount - 2, pageCount - 1);
      } else {
        pages.push(0, "...", page - 1, page, page + 1, "...", pageCount - 1);
      }
    }
    return pages;
  };

  return (
    <div
      className={cn("flex items-center justify-center pt-2", className)}
      data-testid={id ? `${id}-pagination-card` : "pagination-card"}
    >
      <div className="flex items-center gap-2">
        {/* Prev Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(page - 1)}
          disabled={disablePrev}
          title={t("PAGINATION.PREVIOUS")}
          aria-label="previousPage"
          className="px-2 py-1 rounded-md disabled:cursor-not-allowed disabled:opacity-50 border"
          id={`${id}-previous-button`}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {getPageNumbers().map((p, idx) =>
            typeof p === "number" ? (
              <Button
                key={idx}
                variant={p === page ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "px-3 py-1 text-sm border",
                  p === page
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent",
                )}
                onClick={() => onPageChange(p)}
                id={`${id}-page-${p}-button`}
              >
                {p + 1}
              </Button>
            ) : (
              <Typography
                key={idx}
                variant="p"
                className="px-2 text-foreground"
              >
                {p}
              </Typography>
            ),
          )}
        </div>

        {/* Next Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(page + 1)}
          disabled={disableNext}
          title={t("PAGINATION.NEXT")}
          aria-label="nextPage"
          className="px-2 py-1 border rounded-md disabled:cursor-not-allowed disabled:opacity-50"
          id={`${id}-next-button`}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default CommonPaginationCard;
